import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  CircularProgress,
  Fab,
  Container,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import StarRating from "./StarRating";
import { getCardData, reportCategories } from "./ReportDataNew";
import DOMPurify from "dompurify";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import NavigationSidebar from "./NavigationSidebar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  fontFamily: "Poppins",
  fontSize: "16px",
  fontWeight: 400,
  color: "#30313E",
  "&.Mui-selected": {
    fontWeight: 600,
    color: "#41444B",
    backgroundColor: "rgba(223, 116, 104, 0.1)",
    borderRadius: "4px 4px 0 0",
  },
}));

const CategoryTitle = styled(Typography)({
  fontFamily: "Merriweather",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: 1.2,
  color: "#E17A70",
  marginBottom: "8px",
});

const CardDescription = styled(Typography)({
  fontFamily: "Figtree",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: 1.5,
  color: "#41444B",
  marginTop: "16px",
});

const NextStepNumber = styled(Typography)({
  fontFamily: "Poppins",
  fontWeight: 700,
  fontSize: "50px",
  lineHeight: 1.5,
  color: "#DF7468",
  marginRight: "24px",
});

const NextStepTitle = styled(Typography)({
  fontFamily: "Merriweather",
  fontWeight: 900,
  fontSize: "18px",
  lineHeight: 1.11,
  color: "#DF7468",
  whiteSpace: "pre-line",
  marginBottom: "8px",
});

const NextStepText = styled(Typography)({
  fontFamily: "Figtree",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: 1.43,
  color: "#41444B",
});

const AnimalPersona = styled(Box)({
  background: "#FFFFFF",
  padding: "32px",
  borderRadius: "5px",
  marginBottom: "32px",
});

const PersonaTitle = styled(Typography)({
  fontFamily: "Merriweather",
  fontWeight: 700,
  fontSize: "42px",
  lineHeight: 1.25,
  color: "#41444B",
  marginBottom: "16px",
});

const PersonaDescription = styled(Typography)({
  fontFamily: "Figtree",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: 1.5,
  color: "#41444B",
});

const StyledAccordion = styled(Accordion)({
  boxShadow: "none",
  "&:before": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    minHeight: "48px",
    padding: "0",
    "& .MuiAccordionSummary-content": {
      margin: "0",
    },
  },
  "& .MuiAccordionDetails-root": {
    padding: "0",
  },
});

const NavigationButton = styled(Button)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 600,
  fontSize: "16px",
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#FFFFFF",
  color: "#DF7468",
  border: "2px solid #DF7468",
  "&:hover": {
    backgroundColor: "#DF7468",
    color: "#FFFFFF",
  },
}));

const ReportNew = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [report, setReport] = useState(null);
  const [currentCategoryKey, setCurrentCategoryKey] =
    useState("careerOverview");
  const [isLoading, setIsLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [hasAnswers, setHasAnswers] = useState(null);

  useEffect(() => {
    // Check if user has answered questions before fetching report
    const checkUserAnswers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/${userId}/has-answers`
        );
        const data = await response.json();
        if (!data.hasAnswers) {
          navigate(`/questions`, { replace: true, state: { userId } });
        } else {
          setHasAnswers(true);
        }
      } catch (error) {
        navigate(`/questions`, { replace: true, state: { userId } });
      }
    };
    checkUserAnswers();
  }, [userId, navigate]);

  useEffect(() => {
    if (hasAnswers) {
      const fetchReportData = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/useranswers/report/${userId}`
          );
          const data = await response.json();
          setReport(data);
        } catch (error) {
          setReport(null);
        } finally {
          setIsLoading(false);
        }
      };
      fetchReportData();
    }
  }, [userId, hasAnswers]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCategoryChange = (event, newValue) => {
    const categoryKeys = Object.keys(reportCategories);
    setCurrentCategoryKey(categoryKeys[newValue]);
    setMediaLoading({});
  };

  const handleMediaLoad = (cardKey) => {
    setMediaLoading((prev) => ({
      ...prev,
      [cardKey]: false,
    }));
  };

  const handleNavigateCategory = (categoryKey) => {
    const categoryIndex = categoryKeys.indexOf(categoryKey);
    setCurrentCategoryKey(categoryKey);
    setMediaLoading({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Add this new function to handle accordion expansion
  const handleAccordionChange = (cardKey) => (event, expanded) => {
    if (expanded) {
      // Increased timeout to ensure animation completes
      setTimeout(() => {
        const element = document.getElementById(`accordion-${cardKey}`);
        if (element) {
          // Calculate offset for fixed header (AppBar + Tabs height ≈ 120px)
          const offset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 150); // Slightly increased timeout for more reliability
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return <Typography>No report data available</Typography>;
  }

  const cardData = getCardData(report);
  const currentCategory = reportCategories[currentCategoryKey];
  const categoryKeys = Object.keys(reportCategories);
  const currentCategoryIndex = categoryKeys.indexOf(currentCategoryKey);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <NavigationSidebar />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1 }}>
        {/* Top AppBar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: "white",
            mb: 1,
            width: "100%",
          }}
        >
          <Toolbar>
            <img
              src="/images/Logo/cai logo.jpg"
              alt="Company Logo"
              style={{ height: 40 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            {/* <Button
              onClick={() => navigate(`/dashboard/${userId}`)}
              sx={{
                color: "#000000",
                border: "1px solid #000000",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              View Dashboard
            </Button> */}
          </Toolbar>
        </AppBar>

        <Box
          sx={{ bgcolor: "#FDE36F", py: 4, minHeight: "calc(100vh - 73px)" }}
        >
          <Container maxWidth="lg">
            {/* Animal Persona Section */}
            {/* <AnimalPersona>
              <PersonaTitle>You are a LION!</PersonaTitle>
              <PersonaDescription>
                You are confident in your own Career and you stand tall and
                proud. You are agile and strategic, knowing where the best
                opportunities are and should the market turn against your
                favour, you actively seek out new hunting ground with better
                options.
              </PersonaDescription>
            </AnimalPersona> */}

            {/* Category Tabs */}
            <Box
              sx={{
                borderBottom: 0,
                borderColor: "divider",
                mb: 0,
                bgcolor: "white",
                borderRadius: "8px 8px 8px 8px",
                px: 2,
                pt: 2,
              }}
            >
              <Tabs
                value={currentCategoryIndex}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#DF7468",
                  },
                }}
              >
                {categoryKeys.map((key) => (
                  <StyledTab key={key} label={reportCategories[key].title} />
                ))}
              </Tabs>
            </Box>

            {/* Category Content */}
            <Grid container spacing={4}>
              {currentCategory?.cards.map((cardKey, index) => {
                const card = cardData[cardKey];
                if (!card) return null;

                const cardContent = (
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "none",
                      bgcolor: "white",
                      p: 4,
                      border: "1px solid rgba(65, 68, 75, 0.08)",
                    }}
                  >
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            position: "relative",
                            width: index === 0 ? "80%" : "100%",
                            paddingTop: index === 0 ? "80%" : "100%",
                            borderRadius: "50%",
                            overflow: "hidden",
                            mx: index === 0 ? "auto" : "initial",
                          }}
                        >
                          {mediaLoading[cardKey] && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          )}
                          {card.media && (
                            <CardMedia
                              component={
                                card.media.type === "video" ? "video" : "img"
                              }
                              src={card.media.src}
                              poster={
                                card.media.type === "video"
                                  ? card.media.poster
                                  : undefined
                              }
                              controls={card.media.type === "video"}
                              alt={card.title}
                              onLoad={
                                card.media.type !== "video"
                                  ? () => handleMediaLoad(cardKey)
                                  : undefined
                              }
                              onLoadedData={
                                card.media.type === "video"
                                  ? () => handleMediaLoad(cardKey)
                                  : undefined
                              }
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: mediaLoading[cardKey]
                                  ? "none"
                                  : "block",
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Box>
                          <CategoryTitle>{card.title}</CategoryTitle>
                          {card.showStars && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <StarRating score={card.score} />
                            </Box>
                          )}
                          <CardDescription
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(card.text),
                            }}
                          />

                          {/* Next Steps Section */}
                          {card.nextSteps && card.nextSteps.length > 0 && (
                            <Box sx={{ mt: 8 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 4,
                                  bgcolor: "#FFF3C7",
                                  p: 2,
                                  borderRadius: 1,
                                  width: "fit-content",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontWeight: 700,
                                    fontSize: "22px",
                                    lineHeight: 1,
                                    color: "#DF7468",
                                  }}
                                >
                                  Next Steps
                                </Typography>
                                <Box
                                  component="img"
                                  src="/images/arrow-right.svg"
                                  alt=""
                                  sx={{ height: 24 }}
                                />
                              </Box>
                              <Grid container spacing={4}>
                                {card.nextSteps.map((step, index) => (
                                  <Grid item xs={12} md={4} key={index}>
                                    <Box
                                      sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          mb: 3,
                                          height: 80, // Fixed height for title area
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontFamily: "Poppins",
                                            fontWeight: 700,
                                            fontSize: "64px",
                                            lineHeight: 1,
                                            color: "#DF7468",
                                            opacity: 0.2,
                                            mr: 2,
                                            flexShrink: 0,
                                          }}
                                        >
                                          {index + 1}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                          }}
                                        >
                                          <NextStepTitle>
                                            {step.title
                                              .toUpperCase()
                                              .split(" ")
                                              .join("\n")}
                                          </NextStepTitle>
                                        </Box>
                                      </Box>
                                      <Box sx={{ flex: 1 }}>
                                        <NextStepText>{step.body}</NextStepText>
                                      </Box>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                );

                return (
                  <Grid item xs={12} key={cardKey}>
                    {index === 0 ? (
                      cardContent
                    ) : (
                      <StyledAccordion
                        id={`accordion-${cardKey}`}
                        onChange={handleAccordionChange(cardKey)}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            bgcolor: "white",
                            borderRadius: "8px",
                            mb: 1,
                            "& .MuiAccordionSummary-content": {
                              m: 0,
                              p: 2,
                            },
                            "&.Mui-expanded": {
                              minHeight: "0px",
                              height: 0,
                              overflow: "hidden",
                              margin: 0,
                              "& .MuiAccordionSummary-content": {
                                display: "none",
                              },
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Merriweather",
                              fontWeight: 700,
                              fontSize: "24px",
                              color: "#E17A70",
                            }}
                          >
                            {card.title}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>{cardContent}</AccordionDetails>
                      </StyledAccordion>
                    )}
                  </Grid>
                );
              })}
            </Grid>

            {/* Navigation Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 4,
                mb: 2,
              }}
            >
              {currentCategoryIndex > 0 && (
                <NavigationButton
                  onClick={() =>
                    handleNavigateCategory(
                      categoryKeys[currentCategoryIndex - 1]
                    )
                  }
                  startIcon={
                    <ArrowRightAltIcon sx={{ transform: "rotate(180deg)" }} />
                  }
                >
                  {
                    reportCategories[categoryKeys[currentCategoryIndex - 1]]
                      .title
                  }
                </NavigationButton>
              )}
              {currentCategoryIndex < categoryKeys.length - 1 && (
                <NavigationButton
                  onClick={() =>
                    handleNavigateCategory(
                      categoryKeys[currentCategoryIndex + 1]
                    )
                  }
                  endIcon={<ArrowRightAltIcon />}
                >
                  {
                    reportCategories[categoryKeys[currentCategoryIndex + 1]]
                      .title
                  }
                </NavigationButton>
              )}
            </Box>
          </Container>
        </Box>

        {/* Back to Top Button */}
        {showBackToTop && (
          <Fab
            color="primary"
            size="small"
            onClick={handleBackToTop}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              bgcolor: "#FFFFFF",
              color: "#DF7468",
              boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.25)",
              "&:hover": {
                bgcolor: "#FFFFFF",
              },
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        )}
      </Box>
    </Box>
  );
};

export default ReportNew;
